/**
 * HomepageV3 editorial design tokens ("Bordeaux editorial" system).
 *
 * Mirrors the shape of `HeroSectionV2/tokens.ts`. Source of truth:
 * `design-refs/source/assets/private-home.css` (:root) and BUILD_PLAN.md.
 *
 * These are the typed JS tokens consumed by section components via
 * Emotion `styled` / MUI `sx`. The matching CSS custom properties (so ported
 * CSS using `var(--night)` etc. keeps working) live in `globalStyles.ts`.
 */

export const COLORS = {
  // Dark editorial surfaces
  night: '#0B292D',
  night2: '#103A3E',
  nightDeep: '#061819',
  // Light editorial surfaces
  cream: '#FCF9F5',
  cream2: '#F5EFE4',
  card: '#FFFFFF',
  edge: '#E8E1D6',
  // Accents
  gold: '#8C6A2C',
  goldOnLight: '#805F25',
  goldOnDark: '#A98442',
  champagne: '#E9D7B6',
  teal: '#01D1B8',
  tealInk: '#0C6F64',
  wine: '#A33A4C',
  // Ink (text on cream)
  ink: '#221E19',
  ink2: '#38332B',
  ink3: '#514B40',
  // Text on dark surfaces
  onDark: 'rgba(255, 255, 255, 0.96)',
  onDark2: 'rgba(255, 255, 255, 0.84)',
  onDark3: 'rgba(255, 255, 255, 0.70)',
} as const;

export const FONTS = {
  // PD-36055 (Paige #3): IBM Plex Serif/Mono load via next/font (app/layout.jsx)
  // under a hashed family exposed as --font-ibm-plex-serif / --font-ibm-plex-mono;
  // the literal names never matched, so editorial headings/eyebrows silently fell
  // back to Georgia/ui-monospace. Prepend the next/font vars (mirrors the Builder
  // token in BuilderLayout/designTokens.js). Plex Sans loads by name via a Google
  // <link>, so it resolves as-is.
  serif: 'var(--font-ibm-plex-serif), "IBM Plex Serif", Georgia, serif',
  sans: '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  mono: 'var(--font-ibm-plex-mono), "IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
} as const;

export const GRADIENTS = {
  topSurface: 'radial-gradient(120% 85% at 18% 0%, #0D2A2C 0%, #07191A 44%, #030D0C 100%)',
  pastOfferingsTicker: 'radial-gradient(120% 85% at 18% 0%, #0D2A2C 0%, #07191A 44%, #030D0C 100%)',
} as const;

/**
 * Editorial type scale. Headlines use serif; eyebrows / chips use sans/mono.
 * Sizes given in px to match the source CSS 1:1.
 */
export const TYPE = {
  eyebrow: {
    fontFamily: FONTS.sans,
    fontWeight: 600,
    fontSize: '12px',
    letterSpacing: '0.32em',
    textTransform: 'uppercase' as const,
  },
  heroTitle: {
    fontFamily: FONTS.serif,
    fontWeight: 500,
    fontSize: { mobile: '34px', tablet: '48px', desktop: '60px' },
    lineHeight: 1.04,
    letterSpacing: '-0.01em',
  },
  sectionTitle: {
    fontFamily: FONTS.serif,
    fontWeight: 500,
    fontSize: { mobile: '26px', desktop: '34px' },
    lineHeight: 1.1,
  },
  cardName: {
    fontFamily: FONTS.serif,
    fontWeight: 500,
    fontSize: '20px',
    lineHeight: 1.2,
  },
  seriesChip: {
    fontFamily: FONTS.mono,
    fontWeight: 500,
    fontSize: '10px',
    letterSpacing: '0.08em',
  },
  body: {
    fontFamily: FONTS.sans,
    fontWeight: 400,
    fontSize: '15px',
    lineHeight: 1.6,
  },
  bodySm: {
    fontFamily: FONTS.sans,
    fontWeight: 400,
    fontSize: '13px',
    lineHeight: 1.5,
  },
  statValue: {
    fontFamily: FONTS.serif,
    fontWeight: 500,
    fontSize: { mobile: '26px', desktop: '32px' },
    lineHeight: 1.1,
  },
  statLabel: {
    fontFamily: FONTS.sans,
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: 1.4,
  },
  navLink: {
    fontFamily: FONTS.sans,
    fontWeight: 500,
    fontSize: '13.5px',
    letterSpacing: '0.16em',
    textTransform: 'uppercase' as const,
  },
} as const;

export const RADII = {
  xs: '4px',
  sm: '9px',
  md: '14px',
  lg: '18px',
  card: '16px',
  pill: '999px',
} as const;

export const SHADOWS = {
  card: '0 1px 2px rgba(11,41,45,0.04), 0 8px 24px rgba(11,41,45,0.06)',
  cardHover: '0 4px 10px rgba(11,41,45,0.08), 0 18px 44px rgba(11,41,45,0.12)',
  dropdown: '0 22px 50px rgba(0,0,0,0.5)',
  spotlight: '0 30px 80px rgba(0,0,0,0.18)',
} as const;

export const SPACING = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  '2xl': '48px',
  '3xl': '64px',
  section: { mobile: '48px', desktop: '88px' },
} as const;

export const BREAKPOINTS = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
} as const;

export const MEDIA = {
  sm: '@media (min-width: 640px)',
  md: '@media (min-width: 768px)',
  lg: '@media (min-width: 1024px)',
  xl: '@media (min-width: 1280px)',
} as const;

/** Desktop page side padding (`--pad` in the source CSS). */
export const pad = 64;

export type ColorKey = keyof typeof COLORS;
export type RadiiKey = keyof typeof RADII;
export type ShadowKey = keyof typeof SHADOWS;
export type BreakpointKey = keyof typeof BREAKPOINTS;
